import { Route, Routes } from "react-router-dom";
import { lazy, Suspense } from "react";
import AuthCallbackPage from "./auth/AuthCallbackPage";
import { ProtectedRoute } from "./auth/ProtectedRoute";

const AppIndex = lazy(() => import("../pages/index"));
const DomainsPage = lazy(() => import("../pages/domains"));
const BulkImportPage = lazy(() => import("../pages/bulk/index"));
const BuzzPage = lazy(() => import("../pages/buzz/index"));
const HiringTrendsPage = lazy(() => import("../pages/hiring-trends/index"));
const CompanyPage = lazy(() => import("../pages/company/[orgnr]"));
const HeatPage = lazy(() => import("../pages/heat/index"));
const ImportPage = lazy(() => import("../pages/import/index"));
const LikelyMatchPage = lazy(() => import("../pages/likely-match/index"));
const LocationPage = lazy(() => import("../pages/location/[premiseId]"));
const MLPage = lazy(() => import("../pages/ml/index"));
const ReportUploadPage = lazy(() => import("../pages/reports/upload"));
const AnnualReportViewPage = lazy(() => import("../pages/annual-report/view"));
const ClaudeMcpPage = lazy(() => import("../pages/admin/claude-mcp"));
const SourceHealthPage = lazy(() => import("../pages/admin/source-health"));
const CareerScraperPage = lazy(() => import("../pages/admin/career-scraper"));
const PlutoPage = lazy(() => import("../pages/pluto/index"));
const ScoreFilterPage = lazy(() => import("../pages/score-filter/index"));
const ScoreFilterDetailPage = lazy(() => import("../pages/score-filter/[heatFilterId]"));
const ScraperLogsPage = lazy(() => import("../pages/scraper-logs/index"));
const TestHelpersPage = lazy(() => import("../pages/test-helpers"));
const UploadPage = lazy(() => import("../pages/upload/index"));
const ToolsPage = lazy(() => import("../pages/tools/index"));
const ActionTitlePage = lazy(() => import("../pages/tools/action-title/index"));
const AddUseCasePage = lazy(() => import("../pages/tools/add-use-case/index"));
const BookReviewPage = lazy(() => import("../pages/tools/book-review/index"));
const ContentSuggestionsPage = lazy(() => import("../pages/tools/content-suggestions/index"));
const DraftAgendaPage = lazy(() => import("../pages/tools/draft-agenda/index"));
const EnglishTranslatePage = lazy(() => import("../pages/tools/english-translate/index"));
const ExtractTonalityPage = lazy(() => import("../pages/tools/extract-tonality/index"));
const GrammarCheckPage = lazy(() => import("../pages/tools/grammar-check/index"));
const KeywordExtractPage = lazy(() => import("../pages/tools/keyword-extract/index"));
const KeywordIntentPage = lazy(() => import("../pages/tools/keyword-intent/index"));
const MetaDescriptionPage = lazy(() => import("../pages/tools/meta-description/index"));
const MultiProductTitlePage = lazy(() => import("../pages/tools/multi-product-title/index"));
const ProductTitlePage = lazy(() => import("../pages/tools/product-title/index"));
const PromptPage = lazy(() => import("../pages/tools/prompt/index"));
const SalesEmailGeneratorPage = lazy(() => import("../pages/tools/sales-email-generator/index"));
const WriteArticlePage = lazy(() => import("../pages/tools/write-article/index"));
const CLIDocumentationPage = lazy(() => import("../pages/public-documentation-cli/index"));
const LinkedInExtensionPage = lazy(() => import("../pages/linkedin-extension/index"));
const AuthenticatedLandingPage = lazy(() => import("../pages/landing/index"));
const LocationIntelligencePage = lazy(() => import("../pages/location-intelligence/index"));
const WhatsNewPage = lazy(() => import("../pages/whats-new"));

export const AppRoutes = () => (
  <Suspense fallback={null}>
    <Routes>
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/" element={<AppIndex />} />
      <Route path="/cli-documentation" element={<CLIDocumentationPage />} />
      <Route path="/linkedin-extension" element={<LinkedInExtensionPage />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/whats-new" element={<WhatsNewPage />} />
        <Route path="/landing" element={<AuthenticatedLandingPage />} />
        <Route path="/domains" element={<DomainsPage />} />
        <Route path="/bulk" element={<BulkImportPage />} />
        <Route path="/buzz" element={<BuzzPage />} />
        <Route path="/hiring-trends" element={<HiringTrendsPage />} />
        <Route path="/company/:orgnr" element={<CompanyPage />} />
        <Route path="/heat" element={<HeatPage />} />
        <Route path="/import" element={<ImportPage />} />
        <Route path="/likely-match" element={<LikelyMatchPage />} />
        <Route path="/location/:premiseId" element={<LocationPage />} />
        <Route path="/location-intelligence" element={<LocationIntelligencePage />} />
        <Route path="/ml" element={<MLPage />} />
        <Route path="/annual-report/upload" element={<ReportUploadPage />} />
        <Route path="/annual-report/view" element={<AnnualReportViewPage />} />
        <Route path="/admin/claude-mcp" element={<ClaudeMcpPage />} />
        <Route path="/admin/source-health" element={<SourceHealthPage />} />
        <Route path="/admin/career-scraper" element={<CareerScraperPage />} />
        <Route path="/pluto" element={<PlutoPage />} />
        <Route path="/score-filter" element={<ScoreFilterPage />} />
        <Route path="/score-filter/:heatFilterId" element={<ScoreFilterDetailPage />} />
        <Route path="/scraper-logs" element={<ScraperLogsPage />} />
        <Route path="/test-helpers" element={<TestHelpersPage />} />
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/tools" element={<ToolsPage />} />
        <Route path="/tools/action-title" element={<ActionTitlePage />} />
        <Route path="/tools/add-use-case" element={<AddUseCasePage />} />
        <Route path="/tools/book-review" element={<BookReviewPage />} />
        <Route path="/tools/content-suggestions" element={<ContentSuggestionsPage />} />
        <Route path="/tools/draft-agenda" element={<DraftAgendaPage />} />
        <Route path="/tools/english-translate" element={<EnglishTranslatePage />} />
        <Route path="/tools/extract-tonality" element={<ExtractTonalityPage />} />
        <Route path="/tools/grammar-check" element={<GrammarCheckPage />} />
        <Route path="/tools/keyword-extract" element={<KeywordExtractPage />} />
        <Route path="/tools/keyword-intent" element={<KeywordIntentPage />} />
        <Route path="/tools/meta-description" element={<MetaDescriptionPage />} />
        <Route path="/tools/multi-product-title" element={<MultiProductTitlePage />} />
        <Route path="/tools/product-title" element={<ProductTitlePage />} />
        <Route path="/tools/prompt" element={<PromptPage />} />
        <Route path="/tools/sales-email-generator" element={<SalesEmailGeneratorPage />} />
        <Route path="/tools/write-article" element={<WriteArticlePage />} />
      </Route>
    </Routes>
  </Suspense>
);
